// The net-over-time bar chart above the report table.
// It keeps its own window, so a one-day report still shows the month behind it.
import React, { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Caret } from "./ReportParts";
import {
  CHART_LABEL,
  daysBetween,
  dm,
  dmy,
  iso,
  money,
  monthLabel,
  num,
  short,
  weekKey,
} from "./reportFormat";

export const chartWindowFor = (chartRange, from, to, allDates) => {
  const t = dayjs();
  if (chartRange === "match") {
    if (from && to) return [from, to];
    const sorted = allDates.filter(Boolean).slice().sort();
    return sorted.length ? [sorted[0], sorted[sorted.length - 1]] : [iso(t), iso(t)];
  }
  if (chartRange === "ytd") return [iso(t.startOf("year")), iso(t)];
  const n = chartRange === "7d" ? 7 : chartRange === "90d" ? 90 : 30;
  return [iso(t.subtract(n - 1, "day")), iso(t)];
};

const TrendChart = ({
  title,
  rows, // already filtered to the chart window
  dateKey,
  valueKey,
  win,
  bucket,
  onBucketChange,
  chartRange,
  onRangeChange,
}) => {
  const cardRef = useRef(null);
  const [tip, setTip] = useState(null);

  const mode = useMemo(() => {
    if (bucket !== "auto") return bucket;
    const days = daysBetween(win[0], win[1]) + 1;
    return days <= 62 ? "day" : days <= 400 ? "week" : "month";
  }, [bucket, win]);

  const { data, max, top } = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const d = r[dateKey];
      if (!d) return;
      const k = mode === "day" ? d : mode === "week" ? weekKey(d) : String(d).slice(0, 7);
      if (!m[k]) m[k] = { key: k, value: 0, n: 0 };
      m[k].value += num(r[valueKey]);
      m[k].n += 1;
    });
    const list = Object.keys(m)
      .sort()
      .map((k) => m[k]);
    const mx = list.reduce((a, b) => Math.max(a, b.value), 0);
    const nice = Math.pow(10, Math.floor(Math.log10(mx || 1)));
    return { data: list, max: mx, top: Math.ceil((mx || 1) / (nice / 2)) * (nice / 2) || 1 };
  }, [rows, dateKey, valueKey, mode]);

  const step = Math.max(1, Math.ceil(data.length / 12));
  const label = (k, long) =>
    mode === "month"
      ? monthLabel(k)
      : mode === "week" && long
      ? `Week of ${dmy(k)}`
      : long
      ? dmy(k)
      : dm(k);

  const onEnter = (e, i) => {
    const card = cardRef.current?.getBoundingClientRect();
    const b = e.currentTarget.getBoundingClientRect();
    if (!card) return;
    setTip({ i, left: b.left - card.left + b.width / 2, top: b.top - card.top - 8 });
  };
  const tipData = tip ? data[tip.i] : null;

  return (
    <section className="axr-card axr-chartcard" ref={cardRef}>
      <div className="axr-charthead">
        <span className="axr-ct">{title}</span>
        <span className="axr-cs">
          {CHART_LABEL[chartRange]} · {dmy(win[0])} → {dmy(win[1])}
          {data.length ? ` · peak ${money(max)}` : " · nothing to plot"}
        </span>
        <div className="axr-cr">
          <div className={"axr-minisel" + (chartRange !== "match" ? " on" : "")}>
            <select
              value={chartRange}
              onChange={(e) => onRangeChange(e.target.value)}
              aria-label="Trend period"
            >
              <option value="30d">Last 30 days</option>
              <option value="7d">Last 7 days</option>
              <option value="90d">Last 90 days</option>
              <option value="ytd">Year to date</option>
              <option value="match">Match report period</option>
            </select>
            <Caret />
          </div>
          <div className="axr-minisel">
            <select value={bucket} onChange={(e) => onBucketChange(e.target.value)} aria-label="Bucket">
              <option value="auto">Auto</option>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
            <Caret />
          </div>
        </div>
      </div>

      <div className="axr-plot">
        <div className="axr-yaxis">
          {[1, 0.5, 0].map((f) => (
            <span key={f} style={{ top: `${(1 - f) * 100}%` }}>
              {short(top * f)}
            </span>
          ))}
        </div>
        <div className="axr-plotarea">
          <div className="axr-gridlines">
            {[1, 0.5, 0].map((f) => (
              <i key={f} className={f === 0 ? "zero" : ""} style={{ top: `${(1 - f) * 100}%` }} />
            ))}
          </div>
          <div className="axr-bars" onMouseLeave={() => setTip(null)}>
            {data.map((d, i) => (
              <div
                key={d.key}
                className={"axr-bar" + (tip && tip.i !== i ? " dim" : "")}
                style={{ height: `${Math.max(2, (d.value / top) * 100)}%` }}
                onMouseEnter={(e) => onEnter(e, i)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="axr-xaxis">
        {data.map((d, i) => (
          <span key={d.key}>{i % step === 0 ? label(d.key, false) : ""}</span>
        ))}
      </div>

      {tipData && (
        <div className="axr-ctip" style={{ left: tip.left, top: tip.top }}>
          <div className="axr-ctd">{label(tipData.key, true)}</div>
          <div className="axr-ctm">
            {money(tipData.value)}
            <small>
              {tipData.n} record{tipData.n === 1 ? "" : "s"}
            </small>
          </div>
        </div>
      )}
    </section>
  );
};

export default TrendChart;
